// Tiny tween manager. Everything tweens; nothing pops.
export type Ease = (t: number) => number;

export const easeOutCubic: Ease = (t) => 1 - Math.pow(1 - t, 3);
export const easeInOutCubic: Ease = (t) =>
  t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
export const easeOutBack: Ease = (t) => {
  const c1 = 1.70158;
  const c3 = c1 + 1;
  return 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2);
};

interface Tween {
  target: Record<string, number>;
  key: string;
  from: number;
  to: number;
  duration: number;
  elapsed: number;
  ease: Ease;
  onDone?: () => void;
}

export class Tweens {
  private list: Tween[] = [];

  /** Start (or replace) a tween on target[key]. Durations in ms. */
  to(
    target: object,
    key: string,
    to: number,
    duration: number,
    ease: Ease = easeOutCubic,
    opts: { from?: number; onDone?: () => void } = {},
  ): void {
    const t = target as Record<string, number>;
    this.list = this.list.filter((x) => !(x.target === t && x.key === key));
    const from = opts.from ?? t[key] ?? 0;
    t[key] = from;
    const tween: Tween = { target: t, key, from, to, duration, elapsed: 0, ease };
    if (opts.onDone) tween.onDone = opts.onDone;
    this.list.push(tween);
  }

  update(dtMs: number): void {
    const done: Tween[] = [];
    for (const tw of this.list) {
      tw.elapsed += dtMs;
      const p = Math.min(1, tw.elapsed / tw.duration);
      tw.target[tw.key] = tw.from + (tw.to - tw.from) * tw.ease(p);
      if (p >= 1) done.push(tw);
    }
    if (done.length) {
      this.list = this.list.filter((x) => !done.includes(x));
      for (const tw of done) tw.onDone?.();
    }
  }
}
